/**
 * Violation fines, which the sync engine does not carry
 * (back-end-rs/docs/SYNC_ENGINE.md §9).
 *
 * A fine is a ledger charge, and **money stays request/response**: it is
 * read from the REST API, held here in memory for the session, and never
 * written to IndexedDB with the rest of the copy. It would go stale there
 * anyway — a payment changes the fine without touching the violation's
 * row, so no delta would ever correct it.
 *
 * Every REST answer that carries violations feeds this (`recordFines`),
 * and the synced violation hooks lay the fine back onto each row, so a
 * view reads `violation.fine` exactly as it did before the engine.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { violationService } from '../clients/ApiClients';
import { FineReturn, ViolationReturn } from '../clients/rust-api-client';
import { syncStore } from './store';

/** By violation id; `null` is a violation known to have no fine. */
export type Fines = ReadonlyMap<string, FineReturn | null>;

class FineStore {
  private fines: Fines = new Map();
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  get = (): Fines => this.fines;

  record(violations: Pick<ViolationReturn, 'id' | 'fine'>[]): void {
    if (violations.length === 0) return;
    const next = new Map(this.fines);
    violations.forEach((v) => next.set(v.id, v.fine ?? null));
    this.fines = next;
    this.listeners.forEach((l) => l());
  }

  clear(): void {
    this.fines = new Map();
    this.listeners.forEach((l) => l());
  }
}

export const fineStore = new FineStore();

/** Keep the fines of what a REST call returned. */
export const recordFines = (violations: Pick<ViolationReturn, 'id' | 'fine'>[]): void =>
  fineStore.record(violations);

export const useFines = (): Fines => useSyncExternalStore(fineStore.subscribe, fineStore.get);

const useReady = (): boolean => useSyncExternalStore(syncStore.subscribe, syncStore.isReady);

/**
 * For a list showing fines over the synced copy: read them again from
 * REST in the background, once per visit. The rows render at once from
 * the copy, with the fines from earlier in the session; this only
 * refreshes them. Without a copy the list fetches violations itself, and
 * those come with their fines.
 */
export function useFreshFines(scope: { hoaId?: string | null; enabled?: boolean }): void {
  const ready = useReady();
  const { hoaId, enabled = true } = scope;
  useEffect(() => {
    if (!ready || !enabled) return;
    let live = true;
    (hoaId
      ? violationService.getViolationsByHoa({ hoaId })
      : violationService.getAllViolationsForUser()
    )
      .then((r) => live && recordFines(r.data.violations))
      // A list without its fine chips is still a list: no alert.
      .catch((err) => console.error('Error refreshing fines:', err));
    return () => {
      live = false;
    };
  }, [ready, enabled, hoaId]);
}
